import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
    Configurator,
    ConfiguratorsList,
    Price,
    Product,
    ProductAssociationType,
    ProductDetails,
    TaxRatesService,
    handleMainPhotosInConfigurators,
    handleMainPhotosInProducts,
    handlePhotosInConfigurators,
    handlePhotosInProducts,
    handlePricesInProducts,
} from '@icc/b2b/shared';
import { CategoryNode } from './interfaces/category-node.interface';
import { ProductFilter } from './interfaces/product-filter.interface';
import { ReplaySubject, Subscription, filter, firstValueFrom } from 'rxjs';
import { LoaderService, PriceVersionService, SalesDocumentService } from '@icc/b2b/sales-document';
import { PricesDataService } from '@icc/shared/prices-data.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { ExchangeRateService } from '@icc/shared/currency/exchange-rate.service';
import { ConfigService } from '@icc/shared/config.service';

@Injectable({ providedIn: 'root' })
export class ProductsService implements OnDestroy {
    productsList: Product[] = [];
    complaintProductsList: Product[] = [];
    productDetailsLoaded: number[] = [];
    categories: CategoryNode[] = [];
    prices: any;
    configuratorsList: Configurator[] = [];
    productFilters: ProductFilter[];
    productsAssociationsTypes: ProductAssociationType[] = [];
    loaded1: boolean;
    loaded = false;
    loaded$ = new ReplaySubject<boolean>();
    loading = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private http: HttpClient,
        private priceVersionService: PriceVersionService,
        private taxRatesService: TaxRatesService,
        private loaderService: LoaderService,
        private pricesDataService: PricesDataService,
        private salesDocumentService: SalesDocumentService,
        private permissionService: NgxPermissionsService,
        private exchangeRateService: ExchangeRateService,
        private configService: ConfigService,
        private router: Router,
    ) {
        this.subscriptions.push(
            this.salesDocumentService.openedDocument$.subscribe((doc) => {
                if (doc?.uuid && this.loaded1) {
                    this.loadData();
                }
            }),
            permissionService.permissions$.subscribe(() => {
                this.exchangeRateService.loadRates();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    async loadData(force = false) {
        if ((!this.loaded || force) && !this.loading) {
            this.loading = true;
            await Promise.all([
                this.loadProductFilters(),
                this.loadProductCategories(),
                this.loadProductsAssociationsTypes(),
                this.loadConfigurators(),
                firstValueFrom(this.priceVersionService.loaded$),
            ]);
            this.loaded1 = true;
            await this.loadProductsWithPrices();
            await this.loadComplaintProductsWithPrices();
            this.loaded = true;
            this.loaded$.next(true);
            this.loaded$.complete();
            this.loading = false;
        }
    }

    async getData() {
        await this.loaded$.toPromise();
        return {
            productsList: this.productsList,
            complaintProductsList: this.complaintProductsList,
            productFilters: this.productFilters,
            categories: this.categories,
            productsAssociationsTypes: this.productsAssociationsTypes,
            configuratorsList: this.configuratorsList,
        };
    }

    async loadProductsWithPrices() {
        this.loaderService.setLoadingAfterTime();
        const [productList, prices] = await Promise.all([
            this.loadProducts(),
            this.pricesDataService.getProductPricesForPriceVersion(
                this.priceVersionService.activatedPriceVersion?.id,
                this.salesDocumentService.getOpenedDocument()?.buyerCompanyId
            ),
        ]);
        this.productsList = productList;
        this.updatePricesInProducts(this.productsList, prices);
        this.loaderService.setLoaded();
    }

    private async loadComplaintProductsWithPrices() {
        this.loaderService.setLoadingAfterTime();
        const [productList, prices] = await Promise.all([
            this.loadComplaintProducts(),
            this.pricesDataService.getProductPricesForPriceVersion(
                this.priceVersionService.activatedPriceVersion?.id,
                this.salesDocumentService.getOpenedDocument()?.buyerCompanyId
            ),
        ]);
        this.complaintProductsList = productList;
        this.updatePricesInComplaintProducts(this.complaintProductsList, prices);
        this.loaderService.setLoaded();
    }

    loadProducts() {
        const timestamp = Date.now();
        return this.http
            .get<Product[]>(
                `${window.location.origin}/api/b2b/products/b2b?lang=${this.configService.config.languageCode}&marketId=${this.configService.user?.user?.ActiveMarket}&_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then(async (productsList) => {
                productsList = handlePhotosInProducts(productsList);
                productsList = handleMainPhotosInProducts(productsList);
                return productsList;
            });
    }

    loadComplaintProducts() {
        const timestamp = Date.now();
        return this.http
            .get<Product[]>(
                `${window.location.origin}/api/b2b/products/complaints/b2b?lang=${this.configService.config.languageCode}&marketId=${this.configService.user?.user?.ActiveMarket}&_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then(async (productsList) => {
                productsList = handlePhotosInProducts(productsList);
                productsList = handleMainPhotosInProducts(productsList);
                return productsList;
            });
    }

    updatePricesInProducts(productsList: Product[], prices: Record<number, Price>) {
        this.productsList = handlePricesInProducts(productsList, prices);
    }
    updatePricesInComplaintProducts(complaintProductsList: Product[], prices: Record<number, Price>) {
        this.complaintProductsList = handlePricesInProducts(complaintProductsList, prices);
    }

    loadProductFilters() {
        const timestamp = Date.now();
        return this.http
            .get<ProductFilter[]>(
                `${window.location.origin}/api/b2b/product-filters?_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then((productFilters) => {
                this.productFilters = productFilters;
            });
    }

    loadProductCategories() {
        const timestamp = Date.now();
        return this.http
            .get<CategoryNode[]>(
                `${window.location.origin}/api/b2b/product-categories?_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then((categories) => {
                this.categories = categories;
            });
    }

    loadProductsAssociationsTypes() {
        const timestamp = Date.now();
        return this.http
            .get<ProductAssociationType[]>(
                `${window.location.origin}/api/b2b/product-association-types?_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then((productsAssociationsTypes) => {
                this.productsAssociationsTypes = productsAssociationsTypes;
            });
    }

    loadConfigurators() {
        this.configuratorsList = [];
        const timestamp = Date.now();
        return this.http
            .get<ConfiguratorsList>(
                `${window.location.origin}/api/b2b/configurators/b2b?_t=${timestamp}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            )
            .toPromise()
            .then(async (configurators) => {
                const hasPermissionToExternalVenetianData =
                    await this.permissionService.hasPermission(['trade-external-venetian']);
                configurators = configurators.filter((item) => {
                    if (item?.code === 'classic-external-venetian') {
                        return hasPermissionToExternalVenetianData;
                    }
                    return item;
                });
                this.configuratorsList = handlePhotosInConfigurators(configurators);
                this.configuratorsList = handleMainPhotosInConfigurators(configurators);
            });
    }

    setProductDetails(productDetails: ProductDetails) {
        if (productDetails.id && this.productsList.length) {
            const product = this.productsList.find((p) => p.id === productDetails.id);
            if (product) {
                product.description = productDetails.description;
                product.specification = productDetails.specification;
                product.files = productDetails.files;
                product.productFeatures = productDetails.productFeatures;
                product.complexConfigurator = productDetails.complexConfigurator;
                product.showAttributes = productDetails?.showAttributes;
                product.showDetails = productDetails?.showDetails;
            }
        }
    }

    async loadProductDetails(productId: number) {
        if (!this.productDetailsLoaded.includes(productId)) {
            try {
                await this.loaded$.toPromise();
                const timestamp = Date.now();
                const productDetails = await this.http
                    .get<ProductDetails>(
                        `${window.location.origin}/api/b2b/product-details/${productId}?_t=${timestamp}`,
                        {
                            headers: {
                                'Cache-Control': 'no-cache, no-store, must-revalidate',
                                'Pragma': 'no-cache',
                                'Expires': '0'
                            }
                        }
                    )
                    .toPromise();

                if (!productDetails) {
                    await this.router.navigate(['trade']);
                    return;
                }

                this.productDetailsLoaded.push(productId);
                this.setProductDetails(productDetails);
            } catch (error) {
                await this.router.navigate(['trade']);
            }
        }
    }

}
